import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import type { HttpRouteLog } from '../types';
import { listRouteLogs, getRouteLog, type RouteLogListParams } from '../api/http-route-api';

@customElement('http-route-log-view')
export class HttpRouteLogView extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
        sans-serif;
    }

    .page-wrap {
      padding: 1.5rem;
    }

    .panel {
      background: white;
      border-radius: 8px;
      padding: 1.25rem 1.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
      border: 1px solid #e1e4e8;
      margin-bottom: 1.5rem;
    }

    .panel h2 {
      margin: 0 0 1rem;
      font-size: 1rem;
      font-weight: 600;
      color: #102a43;
      padding-bottom: 0.75rem;
      border-bottom: 1px solid #e1e4e8;
    }

    .filter-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 0.75rem;
      margin-bottom: 1rem;
    }

    @media (max-width: 640px) {
      .filter-grid {
        grid-template-columns: 1fr;
      }
    }

    .field {
      display: flex;
      flex-direction: column;
    }

    label {
      display: block;
      margin-bottom: 0.35rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #334e68;
    }

    input[type='text'],
    input[type='datetime-local'],
    select {
      width: 100%;
      box-sizing: border-box;
      padding: 0.5rem 0.65rem;
      border-radius: 6px;
      border: 1px solid #bcccdc;
      font-size: 0.875rem;
      outline: none;
      transition: border-color 0.2s;
    }

    input:focus,
    select:focus {
      border-color: #2684ff;
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0;
    }

    .btn-primary,
    .btn-secondary {
      border: none;
      padding: 0.55rem 1.1rem;
      border-radius: 6px;
      font-weight: 500;
      font-size: 0.875rem;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .btn-primary {
      background-color: #2684ff;
      color: white;
    }

    .btn-primary:hover:not(:disabled) {
      background-color: #005cc5;
    }

    .btn-primary:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .btn-secondary {
      background: white;
      color: #334e68;
      border: 1px solid #bcccdc;
    }

    .btn-secondary:hover:not(:disabled) {
      border-color: #2684ff;
      color: #2684ff;
    }

    .btn-secondary:disabled {
      opacity: 0.65;
      cursor: not-allowed;
    }

    .log-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8125rem;
    }

    .log-table th {
      text-align: left;
      padding: 0.6rem 0.75rem;
      background: #f0f4f8;
      color: #627d98;
      font-weight: 600;
      font-size: 0.6875rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      border-bottom: 2px solid #e1e4e8;
      white-space: nowrap;
    }

    .log-table td {
      padding: 0.55rem 0.75rem;
      border-bottom: 1px solid #f0f4f8;
      color: #334e68;
      max-width: 220px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .log-table tr.log-row {
      cursor: pointer;
      transition: background 0.15s;
    }

    .log-table tr.log-row:hover {
      background: #f0f7ff;
    }

    .log-table tr.log-row.expanded {
      background: #e3f2ff;
    }

    .badge {
      display: inline-block;
      padding: 0.15rem 0.5rem;
      border-radius: 9999px;
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    .badge-success {
      background: #d1fae5;
      color: #065f46;
    }

    .badge-fail {
      background: #fee2e2;
      color: #991b1b;
    }

    .method-badge {
      display: inline-block;
      padding: 0.1rem 0.4rem;
      border-radius: 4px;
      font-size: 0.6875rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      background: #e0e7ff;
      color: #3730a3;
    }

    .detail-panel {
      background: #f8fafc;
      border: 1px solid #d9e2ec;
      border-radius: 6px;
      padding: 1rem;
      margin: 0.5rem 0;
    }

    .detail-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 0.5rem 1.25rem;
    }

    @media (max-width: 640px) {
      .detail-grid {
        grid-template-columns: 1fr;
      }
    }

    .detail-item {
      display: flex;
      flex-direction: column;
      padding: 0.3rem 0;
    }

    .detail-label {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #627d98;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      margin-bottom: 0.15rem;
    }

    .detail-value {
      font-size: 0.8125rem;
      color: #102a43;
      word-break: break-all;
    }

    .detail-value.mono {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.75rem;
      background: #f0f4f8;
      padding: 0.15rem 0.4rem;
      border-radius: 4px;
    }

    .detail-section {
      margin-top: 0.75rem;
      padding-top: 0.75rem;
      border-top: 1px solid #e1e4e8;
    }

    .detail-section-title {
      font-size: 0.75rem;
      font-weight: 600;
      color: #486581;
      margin-bottom: 0.5rem;
    }

    .response-body-box {
      margin: 0;
      padding: 0.75rem;
      background: #102a43;
      color: #e2e8f0;
      border-radius: 6px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.75rem;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-all;
      max-height: 320px;
      overflow: auto;
    }

    .response-body-empty {
      font-size: 0.8125rem;
      color: #829ab1;
    }

    .empty-state {
      text-align: center;
      padding: 2rem 1rem;
      color: #829ab1;
      font-size: 0.875rem;
    }

    .pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      margin-top: 1rem;
      font-size: 0.8125rem;
      color: #486581;
    }

    .pagination button {
      border: 1px solid #bcccdc;
      background: white;
      padding: 0.35rem 0.75rem;
      border-radius: 6px;
      cursor: pointer;
      font-size: 0.8125rem;
      color: #334e68;
      transition: border-color 0.15s, color 0.15s;
    }

    .pagination button:hover:not(:disabled) {
      border-color: #2684ff;
      color: #2684ff;
    }

    .pagination button:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .loading-overlay {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2rem;
      color: #627d98;
      font-size: 0.875rem;
    }
  `;

  /** 筛选字段 */
  @state() private filterSn = '';
  @state() private filterTraceId = '';
  @state() private filterSourcePath = '';
  @state() private filterTargetSystemCode = '';
  @state() private filterSuccess = '';
  @state() private filterStartTime = '';
  @state() private filterEndTime = '';
  @state() private currentPage = 1;
  @state() private pageSize = 20;

  /** 查询结果 */
  @state() private logs: HttpRouteLog[] = [];
  @state() private loading = false;
  @state() private expandedLogId: number | null = null;
  @state() private expandedLog: HttpRouteLog | null = null;
  @state() private expandingLog = false;
  @state() private errorMessage = '';

  protected firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);
    void this.search();
  }

  private async search() {
    this.loading = true;
    this.errorMessage = '';
    try {
      const params: RouteLogListParams = {};
      if (this.filterSn.trim()) params.sn = this.filterSn.trim();
      if (this.filterTraceId.trim()) params.trace_id = this.filterTraceId.trim();
      if (this.filterSourcePath.trim()) params.source_path = this.filterSourcePath.trim();
      if (this.filterTargetSystemCode.trim()) params.target_system_code = this.filterTargetSystemCode.trim();
      if (this.filterSuccess !== '') params.success = this.filterSuccess;
      if (this.filterStartTime) params.start_time = this.filterStartTime;
      if (this.filterEndTime) params.end_time = this.filterEndTime;
      params.page = this.currentPage;
      params.page_size = this.pageSize;

      const result = await listRouteLogs(params);
      this.logs = Array.isArray(result) ? result : [];
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.errorMessage = `查询失败: ${msg}`;
      this.logs = [];
    } finally {
      this.loading = false;
    }
  }

  private handleSearch() {
    this.currentPage = 1;
    this.expandedLogId = null;
    this.expandedLog = null;
    void this.search();
  }

  private handleReset() {
    this.filterSn = '';
    this.filterTraceId = '';
    this.filterSourcePath = '';
    this.filterTargetSystemCode = '';
    this.filterSuccess = '';
    this.filterStartTime = '';
    this.filterEndTime = '';
    this.currentPage = 1;
    this.expandedLogId = null;
    this.expandedLog = null;
    this.errorMessage = '';
    void this.search();
  }

  private async toggleExpand(log: HttpRouteLog) {
    if (this.expandedLogId === log.id) {
      this.expandedLogId = null;
      this.expandedLog = null;
      return;
    }
    this.expandedLogId = log.id;
    this.expandingLog = true;
    try {
      const detail = await getRouteLog(log.id);
      this.expandedLog = detail;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('获取日志详情失败:', msg);
      this.expandedLog = log; // fallback to list row data
    } finally {
      this.expandingLog = false;
    }
  }

  private goPage(page: number) {
    this.currentPage = page;
    this.expandedLogId = null;
    this.expandedLog = null;
    void this.search();
  }

  private formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  }

  private formatTime(iso: string): string {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return iso;
    }
  }

  private formatResponseBody(raw: string | undefined): string {
    const text = (raw ?? '').trim();
    if (!text) return '';
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return text;
    }
  }

  private renderFilterBar() {
    return html`
      <div class="panel">
        <h2>筛选条件</h2>
        <div class="filter-grid">
          <div class="field">
            <label for="f-sn">设备 SN</label>
            <input
              id="f-sn"
              type="text"
              .value=${this.filterSn}
              placeholder="例如 CAR-001"
              @input=${(e: Event) => { this.filterSn = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.handleSearch(); }}
            />
          </div>
          <div class="field">
            <label for="f-trace">Trace ID</label>
            <input
              id="f-trace"
              type="text"
              .value=${this.filterTraceId}
              placeholder="完整或部分 Trace ID"
              @input=${(e: Event) => { this.filterTraceId = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.handleSearch(); }}
            />
          </div>
          <div class="field">
            <label for="f-path">源路径</label>
            <input
              id="f-path"
              type="text"
              .value=${this.filterSourcePath}
              placeholder="例如 /api/car-api/"
              @input=${(e: Event) => { this.filterSourcePath = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.handleSearch(); }}
            />
          </div>
          <div class="field">
            <label for="f-system">目标系统</label>
            <input
              id="f-system"
              type="text"
              .value=${this.filterTargetSystemCode}
              placeholder="系统代码"
              @input=${(e: Event) => { this.filterTargetSystemCode = (e.target as HTMLInputElement).value; }}
              @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') this.handleSearch(); }}
            />
          </div>
          <div class="field">
            <label for="f-success">转发结果</label>
            <select
              id="f-success"
              .value=${this.filterSuccess}
              @change=${(e: Event) => { this.filterSuccess = (e.target as HTMLSelectElement).value; }}
            >
              <option value="">全部</option>
              <option value="1">成功</option>
              <option value="0">失败</option>
            </select>
          </div>
          <div class="field">
            <label for="f-start">开始时间</label>
            <input
              id="f-start"
              type="datetime-local"
              .value=${this.filterStartTime}
              @input=${(e: Event) => { this.filterStartTime = (e.target as HTMLInputElement).value; }}
            />
          </div>
          <div class="field">
            <label for="f-end">结束时间</label>
            <input
              id="f-end"
              type="datetime-local"
              .value=${this.filterEndTime}
              @input=${(e: Event) => { this.filterEndTime = (e.target as HTMLInputElement).value; }}
            />
          </div>
        </div>
        <div class="actions">
          <button type="button" class="btn-primary" ?disabled=${this.loading} @click=${() => this.handleSearch()}>
            ${this.loading ? '查询中…' : '查询'}
          </button>
          <button type="button" class="btn-secondary" ?disabled=${this.loading} @click=${() => this.handleReset()}>
            重置
          </button>
        </div>
      </div>
    `;
  }

  private renderDetail(log: HttpRouteLog) {
    return html`
      <div class="detail-panel">
        <div class="detail-grid">
          <div class="detail-item">
            <span class="detail-label">ID</span>
            <span class="detail-value">${log.id}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Trace ID</span>
            <span class="detail-value mono">${log.trace_id}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">设备 SN</span>
            <span class="detail-value">${log.sn || '—'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">租户 ID</span>
            <span class="detail-value">${log.tenant_id || '—'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">项目 ID</span>
            <span class="detail-value">${log.project_id || '—'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">目标系统</span>
            <span class="detail-value">${log.target_system_code || '—'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">路由规则 ID</span>
            <span class="detail-value">${log.route_rule_id ?? '—'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">持续时间</span>
            <span class="detail-value">${this.formatDuration(log.duration_ms)}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">目标状态码</span>
            <span class="detail-value">${log.target_status_code || '—'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">最终响应码</span>
            <span class="detail-value">${log.final_code || '—'}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">请求大小</span>
            <span class="detail-value">${log.request_size ?? '—'} bytes</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">响应大小</span>
            <span class="detail-value">${log.response_size ?? '—'} bytes</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">请求时间</span>
            <span class="detail-value">${this.formatTime(log.request_time)}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">响应时间</span>
            <span class="detail-value">${this.formatTime(log.response_time)}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">记录时间</span>
            <span class="detail-value">${this.formatTime(log.created_at)}</span>
          </div>
        </div>

        <div class="detail-section">
          <div class="detail-section-title">请求信息</div>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">方法 + 路径</span>
              <span class="detail-value mono">${log.method} ${log.source_path}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Query 参数</span>
              <span class="detail-value mono">${log.query_string || '—'}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">转发目标 URL</span>
              <span class="detail-value mono">${log.target_url || '—'}</span>
            </div>
          </div>
        </div>

        ${log.error_message ? html`
          <div class="detail-section">
            <div class="detail-section-title">错误信息</div>
            <div class="detail-value" style="color:#991b1b;">${log.error_message}</div>
          </div>
        ` : ''}

        <div class="detail-section">
          <div class="detail-section-title">原始响应体</div>
          ${log.response_body
            ? html`<pre class="response-body-box">${this.formatResponseBody(log.response_body)}</pre>`
            : html`<div class="response-body-empty">无记录（旧日志或未产生响应体）</div>`}
          <div class="detail-value" style="margin-top:0.35rem;font-size:0.75rem;color:#627d98;">
            上游或网关返回的原始内容；passthrough 模式与车端所见一致
          </div>
        </div>
      </div>
    `;
  }

  private renderTable() {
    if (this.loading) {
      return html`<div class="loading-overlay">查询中…</div>`;
    }

    if (this.errorMessage) {
      return html`<div class="empty-state">${this.errorMessage}</div>`;
    }

    if (this.logs.length === 0) {
      return html`<div class="empty-state">暂无路由日志数据</div>`;
    }

    return html`
      <div style="overflow-x: auto;">
        <table class="log-table">
          <thead>
            <tr>
              <th>Trace ID</th>
              <th>SN</th>
              <th>方法</th>
              <th>源路径</th>
              <th>目标系统</th>
              <th>耗时</th>
              <th>目标状态码</th>
              <th>结果</th>
              <th>时间</th>
            </tr>
          </thead>
          <tbody>
            ${this.logs.map((log) => html`
              <tr
                class="log-row ${this.expandedLogId === log.id ? 'expanded' : ''}"
                @click=${() => this.toggleExpand(log)}
              >
                <td><span class="detail-value mono" style="font-size:0.75rem;">${log.trace_id ? log.trace_id.slice(0, 12) + '…' : '—'}</span></td>
                <td>${log.sn || '—'}</td>
                <td><span class="method-badge">${log.method}</span></td>
                <td>${log.source_path || '—'}</td>
                <td>${log.target_system_code || '—'}</td>
                <td>${this.formatDuration(log.duration_ms)}</td>
                <td>${log.target_status_code || '—'}</td>
                <td>
                  <span class="badge ${log.success === 1 ? 'badge-success' : 'badge-fail'}">
                    ${log.success === 1 ? '成功' : '失败'}
                  </span>
                </td>
                <td>${this.formatTime(log.created_at)}</td>
              </tr>
              ${this.expandedLogId === log.id ? html`
                <tr>
                  <td colspan="9" style="padding:0; border-bottom:2px solid #90cdf4;">
                    ${this.expandingLog
                      ? html`<div class="loading-overlay" style="padding:1rem;">加载详情中…</div>`
                      : this.expandedLog ? this.renderDetail(this.expandedLog) : ''}
                  </td>
                </tr>
              ` : ''}
            `)}
          </tbody>
        </table>
      </div>

      <div class="pagination">
        <button
          type="button"
          ?disabled=${this.currentPage <= 1 || this.loading}
          @click=${() => this.goPage(this.currentPage - 1)}
        >
          上一页
        </button>
        <span>第 ${this.currentPage} 页</span>
        <button
          type="button"
          ?disabled=${this.logs.length < this.pageSize || this.loading}
          @click=${() => this.goPage(this.currentPage + 1)}
        >
          下一页
        </button>
      </div>
    `;
  }

  render() {
    return html`
      <div class="page-wrap">
        ${this.renderFilterBar()}

        <div class="panel">
          <h2>路由日志</h2>
          ${this.renderTable()}
        </div>
      </div>
    `;
  }
}
